import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { registerForWebinar, getWebinarStatus } from '../../api/client';
import SocialProof from '../Marketing/SocialProof';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const WALK_AWAY = [
  'The one message your audience will actually remember',
  'A repeatable way to prep any high-stakes presentation',
  "Rich's live feedback on a real problem, maybe yours",
];

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [problem, setProblem] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    getWebinarStatus().then(setCampaign).catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setLoading(true);
    try {
      await registerForWebinar(name.trim(), email.trim(), problem.trim());
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  const dateStr = formatDate(campaign?.date);
  const endsStr = formatDate(campaign?.discountEnds);
  const timeStr = campaign?.time || '';
  const pct = campaign?.discountPercent || 50;

  return (
    <div className="min-h-screen bg-navy flex flex-col items-center">
      {/* Top bar (matches the landing page) */}
      <div className="w-full border-b border-white/10">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/bcd-icon.png" alt="BCD" className="h-7 w-auto" />
            <span className="text-white font-heading font-semibold text-lg">The Clarity Map</span>
          </Link>
          <Link to="/login" className="text-gray-200 text-sm font-semibold hover:text-gold">
            Sign in
          </Link>
        </div>
      </div>

      <div className="w-full max-w-5xl px-6 py-12">
        {done ? (
          <div className="w-full max-w-md mx-auto text-center text-pretty">
            <h1 className="text-3xl font-heading font-bold text-white uppercase tracking-wide mb-3">
              You're registered
            </h1>
            <p className="text-gray-300 text-base mb-6 leading-relaxed">
              Thanks for signing up for the live Clarity Map webinar{dateStr ? ` on ${dateStr}${timeStr ? ` at ${timeStr}` : ''}` : ''}.
              We just emailed you a calendar invitation with the join link, so you can add it to your calendar.
            </p>
            <div className="bg-gold/10 border border-gold/40 rounded-xl px-5 py-4 mb-6 flex items-start gap-3 text-left">
              <svg className="w-6 h-6 text-gold flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
              <p className="text-white text-sm leading-snug">
                <span className="font-semibold">Check your inbox for your invitation.</span> If you don't see it within a few minutes, check your spam or junk folder (it comes from Clarity Map).
              </p>
            </div>
            <div className="bg-navy-light border border-gold/20 rounded-xl px-6 py-5 mb-6">
              <p className="text-white font-semibold mb-1">Your {pct}% discount</p>
              <p className="text-gray-300 text-sm leading-relaxed">
                Attend the live webinar to unlock {pct}% off your subscription. We'll share
                your discount code during the session{endsStr ? `; it's good through ${endsStr}` : ''}.
              </p>
            </div>
            <p className="text-gray-300 text-sm mb-3">Want a head start? Try Clarity Map free while you wait.</p>
            <Link to="/signup" className="btn-gold inline-block w-full py-3.5 text-base font-semibold">
              Start your free trial
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-col lg:flex-row lg:items-start gap-10 lg:gap-14">
              {/* LEFT: the pitch */}
              <div className="flex-1 text-center lg:text-left">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-white uppercase tracking-wide leading-tight mb-3">
                  Join the live Clarity Map webinar
                </h1>
                {dateStr && (
                  <p className="text-gold text-xl font-heading font-bold uppercase tracking-wide mb-2">
                    {dateStr}{timeStr ? ` · ${timeStr}` : ''}
                  </p>
                )}
                <p className="text-gray-300 text-sm mb-6">
                  Hosted by Rich Carr, Founder of Brain-Centric Design
                </p>
                <p className="text-gray-300 text-lg mb-8 leading-relaxed max-w-md mx-auto lg:mx-0">
                  Watch Rich build a Clarity Map live and work through real audience problems.
                </p>

                {/* What you'll walk away with */}
                <p className="text-lg font-heading font-bold uppercase tracking-widest text-gold mb-4">
                  What you'll walk away with
                </p>
                <div className="bg-navy-light border border-white/10 rounded-2xl divide-y divide-white/10 max-w-md mx-auto lg:mx-0 text-left">
                  {WALK_AWAY.map((item) => (
                    <div key={item} className="flex items-center gap-4 px-5 py-4">
                      <span className="flex-shrink-0 w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      </span>
                      <span className="text-white text-base leading-snug text-pretty">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT: discount + form */}
              <div className="w-full lg:w-96 lg:flex-shrink-0">
                {/* Discount call-out */}
                <div className="flex items-center gap-3 bg-gold/10 border border-gold/40 rounded-xl px-5 py-4 mb-5">
                  <svg className="w-7 h-7 text-gold flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z" />
                  </svg>
                  <p className="text-white text-sm leading-snug">
                    <span className="text-gold font-bold">Attend live and get {pct}% off</span> a Clarity Map subscription{endsStr ? `, good through ${endsStr}` : ''}.
                  </p>
                </div>

                {/* Form card */}
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-7">
                  <h2 className="text-xl font-heading font-bold text-txt-primary mb-1">Reserve your spot</h2>
                  <p className="text-sm text-txt-secondary mb-5">Free and live. No credit card required.</p>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <input
                      type="text"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                    <input
                      type="email"
                      placeholder="Your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                    <div>
                      <label htmlFor="problem" className="block text-txt-primary font-semibold text-sm mb-1">
                        What's your next big presentation or pitch?
                      </label>
                      <p className="text-xs text-txt-secondary mb-2">
                        Tell us about it and Rich may work through it live on the call.
                      </p>
                      <textarea
                        id="problem"
                        placeholder="e.g., a board update, an investor pitch, a sales presentation, a keynote..."
                        value={problem}
                        onChange={(e) => setProblem(e.target.value)}
                        rows={4}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
                      />
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-gold w-full py-3.5 text-base font-semibold disabled:opacity-50"
                    >
                      {loading ? 'Registering...' : 'Register for the webinar'}
                    </button>
                  </form>
                </div>

                <p className="text-center text-gray-400 text-sm mt-4">
                  Want to start now?{' '}
                  <Link to="/signup" className="text-gold font-semibold hover:underline">Start your free trial</Link>
                </p>
              </div>
            </div>

            {/* Social proof - full width below the two columns */}
            <div className="flex justify-center mt-16">
              <SocialProof />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
